import { useMemo } from 'react'
import {
  IAboutTeachers,
  ISchoolPage,
  ISchoolPageAbout,
  ISchoolPageAboutPlatform,
  ISchoolPageDefaultData,
  ISchoolPageFeedbacks,
  ISchoolPageHero,
  ISchoolPageIndicators,
  ISchoolPageOffer,
  ISchoolPageQuestions,
  ISchoolPageSeo,
  ISchoolPageTariffs,
} from '../types'

export interface ISchoolDataContent {
  seo: ISchoolPageSeo | null
  hero: ISchoolPageHero | null
  indicators: ISchoolPageIndicators | null
  about: ISchoolPageAbout | null
  aboutPlatform: ISchoolPageAboutPlatform | null
  offer: ISchoolPageOffer | null
  tariffs: ISchoolPageTariffs | null
  teachers: IAboutTeachers | null
  feedbacks: ISchoolPageFeedbacks | null
  questions: ISchoolPageQuestions | null
  personalDate: string | null
}

export interface IUseConvertSchoolData {
  (
    data: ISchoolPage | null,
    options?: ISchoolPageDefaultData
  ): {
    content: ISchoolDataContent | null
  }
}

/* функция для мержда данных страниц школы */
export const mergeSchoolData = <T extends ISchoolPageDefaultData>(
  data?: T[],
  options?: ISchoolPageDefaultData
): T | null => {
  const { type, subject, classValue } = options || {}

  if (!data) return null
  let res = data.find((item) => item.type === 'default')

  if (type) {
    const typeValueData = data.find((item) => item.type === type)

    if (typeValueData) {
      res = { ...res, ...typeValueData }
    }
  }

  if (classValue) {
    const classValueData = data.find((item) => {
      if (type) {
        return item.classValue === classValue && item.type === type
      }

      return item.classValue === classValue
    })

    if (classValueData) {
      res = { ...res, ...classValueData }
    }
  }

  if (subject) {
    const subjectValueData = data.find((item) => {
      if (type && classValue) {
        return (
          item.subject === subject && type === item.type && item.classValue === classValue
        )
      }

      if (type) {
        return item.subject === subject && type === item.type
      }

      if (classValue) {
        return item.subject === subject && item.classValue === classValue
      }

      return item.subject === subject
    })

    if (subjectValueData) {
      res = { ...res, ...subjectValueData }
    }
  }

  if (res) return res
  return null
}

/* Хук для получения смердженных данных для страниц школы */
export const useConvertSchoolData: IUseConvertSchoolData = (data, options) => {
  const {
    seo,
    hero,
    aboutPlatform,
    about,
    indicators,
    offer,
    personalDate,
    questions,
    tariffs,
    teachers,
    feedbacks,
  } = data || {}

  const content = useMemo(() => {
    if (data) {
      return {
        seo: mergeSchoolData<ISchoolPageSeo>(seo, options),
        hero: mergeSchoolData<ISchoolPageHero>(hero, options),
        about: mergeSchoolData<ISchoolPageAbout>(about, options),
        offer: mergeSchoolData<ISchoolPageOffer>(offer, options),
        questions: mergeSchoolData<ISchoolPageQuestions>(questions, options),
        tariffs: mergeSchoolData<ISchoolPageTariffs>(tariffs, options),
        teachers: teachers || null,
        feedbacks: feedbacks || null,
        personalDate: personalDate || null,
        indicators: indicators || null,
        aboutPlatform: aboutPlatform || null,
      }
    }

    return null
  }, [data, options, mergeSchoolData])

  return {
    content,
  }
}
